export const colors = {
  primary: 'var(--voyager-color-primary, #85A2AA)',
  secondary: 'var(--voyager-color-secondary, #455B61)',
  tertiary: 'var(--voyager-color-tertiary, #B5D3DC)',

  fontPrimary: 'var(--voyager-color-fontPrimary, #444444)',
  fontSecondary: 'var(--voyager-color-fontSecondary, #666666)',

  navigationAndFooter: 'var(--voyager-color-navigationAndFooter, #333333)',
  header: 'var(--voyager-color-header, #F4F4F4)',
  sectionTitle: 'var(--voyager-color-sectionTitle, #919191)',
  borderGrey: 'var(--voyager-color-borderGrey, #E0E0E0)',
  borderGreyLighter: 'var(--voyager-color-borderGreyLighter, #EFEFEF)',

  darkGrey: 'var(--voyager-color-darkGrey, #646464)',
  grey: 'var(--voyager-color-grey, #C1C1C1)',
  lightGrey: 'var(--voyager-color-lightGrey, #E2E2E2)',
  lighterGrey: 'var(--voyager-color-lighterGrey, #F7F7F7)',
  error: 'var(--voyager-color-error, #E55646)',
  errorBackground: 'var(--voyager-color-errorBackground, #FDEEEB)',
  success: 'var(--voyager-color-success, #009688)',
  successBackground: 'var(--voyager-color-successBackground, #E5F4F3)',
  info: 'var(--voyager-color-info, #01579B)',
  infoBackground: 'var(--voyager-color-infoBackground, #E6EEF5)',
  black: 'var(--voyager-color-black, #0d0d0d)',
  white: 'var(--voyager-color-white, #FFFFFF)',
  offWhite: 'var(--voyager-color-offWhite, #f1f1f1)',
  sale: 'var(--voyager-color-sale, #E2363F)',
} as const;

export const space = {
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '24px',
  6: '32px',
  7: '40px',
  8: '48px',
  9: '64px',
  10: '80px',
  11: '96px',
  '2px': '2px',
  '20px': '20px',
  '36px': '36px',
  '56px': '56px',
  '120px': '120px',
} as const;

export const fonts = {
  heading: "var(--voyager-heading-font, 'Montserrat'), sans-serif",
  body: "var(--voyager-body-font, 'Open Sans'), sans-serif",
  baseSize: '16px',
  size: {
    xxs: '0.5625rem', // 9px
    xs: '0.8125rem', // 13px
    sm: '0.875rem', // 14px
    md: '0.9375rem', // 15px
    base: '1rem', // 16px
    lg: '1.25rem', // 20px
    xl: '1.5rem', // 24px
    '2xl': '1.75rem', // 28px
    '3xl': '2.5rem', // 40px
    '4xl': '3rem', // 48px
  },
  weight: {
    normal: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },
} as const;

export const headingFont = fonts.heading;
export const bodyFont = fonts.body;

export const breakpoints = {
  mobile: '576px',
  tablet: '768px',
  desktop: '992px',
  wide: '1200px',
} as const;

export const layout = {
  maxWidth: '1480px',
  narrowMaxWidth: '860px',
  horizontalPadding: '20px',
  headerHeightMobile: '81px',
  headerHeightDesktop: '111px',
  mobileNavHeight: '64px',
  sideNavWidth: '72px',
  heroImageMobileHeight: '260px',
  desktopBreakpoint: '769px',
  inputPadding: '10px 12px',
  borderRadius: '4px',
  borderRadiusMd: '8px',
  borderRadiusLg: '12px',
  borderRadiusXl: '16px',
  borderRadiusFull: '9999px',
  zIndexDropdown: 1000,
  zIndexModal: 10000,
  modalWidth: '420px',
  overlay: 'rgba(13, 13, 13, 0.2)',
  shadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
} as const;

export const blur = {
  sm: '16px',
  lg: '40px',
} as const;

export const depth = {
  xs: '4px',
} as const;

export const shadow = {
  sm: '0 1px 2px 0 rgba(13, 13, 13, 0.08), 0 1px 3px 0 rgba(13, 13, 13, 0.06)',
  lg: '0 8px 24px -4px rgba(13, 13, 13, 0.16), 0 4px 8px -4px rgba(13, 13, 13, 0.08)',
} as const;

export const primitives = {
  color: {
    blackAlpha: {
      50: '#0d0d0d0d',
      100: '#0d0d0d1a',
      200: '#0d0d0d33',
      400: '#0d0d0d66',
      600: '#0d0d0d99',
      1000: '#0d0d0d',
    },
    whiteAlpha: {
      50: '#ffffff0d',
      100: '#ffffff1a',
      1000: '#ffffff',
    },
  },
  typography: {
    fontFamily: {
      brandPrimary: "var(--voyager-heading-font, 'PP Eiko'), serif",
      brandSecondary: "var(--voyager-body-font, 'Suisse Intl'), sans-serif",
    },
    fontWeight: {
      regular: 400,
      medium: 500,
    },
    tracking: {
      normal: '0',
      tight: '-0.5px',
    },
    lineHeight: {
      tight: 1.2,
      loose: 1.4,
    },
  },
} as const;

export const semantic = {
  text: {
    primary: 'var(--voyager-text-primary, #0d0d0d)',
    secondary: 'var(--voyager-text-secondary, #0d0d0d99)',
    tertiary: 'var(--voyager-text-tertiary, #0d0d0d66)',
    inverse: 'var(--voyager-text-inverse, #ffffff)',
    disabled: 'var(--voyager-text-disabled, #0d0d0d1a)',
    danger: 'var(--voyager-text-danger, #be0e2c)',
    positive: 'var(--voyager-text-positive, #178c3c)',
    info: 'var(--voyager-text-info, #184cb4)',
    warning: 'var(--voyager-text-warning, #8a4b00)',
  },
  bg: {
    primary: 'var(--voyager-bg-primary, #0d0d0d)',
    secondary: 'var(--voyager-bg-secondary, #ffffff)',
    canvas: 'var(--voyager-bg-canvas, #ffffff)',
    brand: 'var(--voyager-bg-brand, var(--voyager-color-primary, #440f95))',
    surfaceSubtle: 'var(--voyager-bg-surface-subtle, #0d0d0d0d)',
    surfaceStrong: 'var(--voyager-bg-surface-strong, #0d0d0d1a)',
    overlayBlur: 'var(--voyager-bg-overlay-blur, #ffffff0d)',
    overlayImage: 'var(--voyager-bg-overlay-image, #0d0d0d1a)',
    overlayModal: 'var(--voyager-bg-overlay-modal, #0d0d0d33)',
    infoSubtle: 'var(--voyager-bg-info-subtle, #e6f0fe)',
    infoStrong: 'var(--voyager-bg-info-strong, #184cb4)',
    positiveSubtle: 'var(--voyager-bg-positive-subtle, #e7fded)',
    positiveStrong: 'var(--voyager-bg-positive-strong, #0c6e2a)',
    dangerSubtle: 'var(--voyager-bg-danger-subtle, #fee6e9)',
    dangerStrong: 'var(--voyager-bg-danger-strong, #be0e2c)',
    warningSubtle: 'var(--voyager-bg-warning-subtle, #fff4e0)',
    warningStrong: 'var(--voyager-bg-warning-strong, #a15a00)',
    urgencyStrong: 'var(--voyager-bg-urgency-strong, #980b23)',
  },
  border: {
    default: 'var(--voyager-border-default, #0d0d0d1a)',
    subtle: 'var(--voyager-border-subtle, #0d0d0d0d)',
    inverse: 'var(--voyager-border-inverse, #ffffff)',
    selected: 'var(--voyager-border-selected, #0d0d0d)',
    danger: 'var(--voyager-border-danger, #980b23)',
    positive: 'var(--voyager-border-positive, #0c6e2a)',
    warning: 'var(--voyager-border-warning, #a15a00)',
    info: 'var(--voyager-border-info, #184cb4)',
  },
  icon: {
    primary: 'var(--voyager-icon-primary, #0d0d0d)',
    secondary: 'var(--voyager-icon-secondary, #0d0d0d99)',
    inverse: 'var(--voyager-icon-inverse, #ffffff)',
    disabled: 'var(--voyager-icon-disabled, #0d0d0d1a)',
    danger: 'var(--voyager-icon-danger, #be0e2c)',
    info: 'var(--voyager-icon-info, #184cb4)',
  },
} as const;

export const colorVarNames = {
  primary: '--voyager-color-primary',
  secondary: '--voyager-color-secondary',
  tertiary: '--voyager-color-tertiary',
  fontPrimary: '--voyager-color-fontPrimary',
  fontSecondary: '--voyager-color-fontSecondary',
  navigationAndFooter: '--voyager-color-navigationAndFooter',
  header: '--voyager-color-header',
  sectionTitle: '--voyager-color-sectionTitle',
  borderGrey: '--voyager-color-borderGrey',
  borderGreyLighter: '--voyager-color-borderGreyLighter',
  darkGrey: '--voyager-color-darkGrey',
  grey: '--voyager-color-grey',
  lightGrey: '--voyager-color-lightGrey',
  lighterGrey: '--voyager-color-lighterGrey',
  error: '--voyager-color-error',
  errorBackground: '--voyager-color-errorBackground',
  success: '--voyager-color-success',
  successBackground: '--voyager-color-successBackground',
  info: '--voyager-color-info',
  infoBackground: '--voyager-color-infoBackground',
  black: '--voyager-color-black',
  white: '--voyager-color-white',
  offWhite: '--voyager-color-offWhite',
  sale: '--voyager-color-sale',
} as const;

export const fontVarNames = {
  heading: '--voyager-heading-font',
  body: '--voyager-body-font',
} as const;
